/**
 * Shared boot fetches for HOMS sales screens.
 * Global init and page loaders share the same in-flight/TTL cache so
 * AppDataInitializer and SalesPipeline/ViewOrders don't double-fetch.
 */
import apiClient from "./apiClient";
import { queryFetch, invalidateQueries } from "../utils/asyncQueryCache";
import { ORDERS_PAGE_SIZE } from "./ordersConstants";
import { QUOTATIONS_PAGE_SIZE } from "./quotationsConstants";
import { DELIVERIES_PAGE_SIZE } from "./deliveriesConstants";
import { INVOICES_PAGE_SIZE } from "./invoicesConstants";
import { CUSTOMERS_PAGE_SIZE } from "./customersConstants";

const BOOT_TTL_MS = 30000;

export const salesBootKeys = {
  orders: (start = 0, size = ORDERS_PAGE_SIZE) => `boot:orders:${start}:${size}`,
  customers: (status = "Active", size = CUSTOMERS_PAGE_SIZE) =>
    `boot:customers:${String(status).toLowerCase()}:${size}`,
  quotations: (size = QUOTATIONS_PAGE_SIZE) => `boot:quotations:0:${size}`,
  deliveries: (size = DELIVERIES_PAGE_SIZE) => `boot:deliveries:0:${size}`,
  invoices: (size = INVOICES_PAGE_SIZE) => `boot:invoices:0:${size}`,
};

export function invalidateSalesBoot(segment = "") {
  invalidateQueries(segment ? `boot:${segment}` : "boot");
}

export async function bootFetchOrders({
  start = 0,
  size = ORDERS_PAGE_SIZE,
  force = false,
  hideLoader = false,
  context = "Loading Orders",
} = {}) {
  return queryFetch(
    salesBootKeys.orders(start, size),
    () =>
      apiClient.get(`/orders?start=${start}&size=${size}`, {
        context: hideLoader ? "Global Init: Orders" : context,
        silent: hideLoader,
        hideLoader,
      }),
    { force, ttlMs: BOOT_TTL_MS }
  );
}

export async function bootFetchCustomers({
  size = CUSTOMERS_PAGE_SIZE,
  status = "Active",
  force = false,
  hideLoader = false,
  context = "Syncing Customers",
} = {}) {
  return queryFetch(
    salesBootKeys.customers(status, size),
    () =>
      apiClient.get(
        `/customers?status=${encodeURIComponent(status)}&start=0&size=${size}`,
        {
          context: hideLoader ? "Global Init: Customers" : context,
          silent: hideLoader,
          hideLoader,
        }
      ),
    { force, ttlMs: BOOT_TTL_MS }
  );
}

export async function bootFetchQuotations({
  size = QUOTATIONS_PAGE_SIZE,
  force = false,
  hideLoader = true,
  status = null,
} = {}) {
  const { getQuotations } = await import("../apis/quotation.api");
  const key = status
    ? `boot:quotations:status:${status}:${size}`
    : salesBootKeys.quotations(size);
  return queryFetch(
    key,
    () =>
      getQuotations({
        status: status || undefined,
        start: 0,
        size,
        hideLoader,
        silent: hideLoader,
      }),
    { force, ttlMs: BOOT_TTL_MS }
  );
}

export async function bootFetchDeliveries({
  size = DELIVERIES_PAGE_SIZE,
  force = false,
  hideLoader = true,
  status = null,
} = {}) {
  const { getDeliveries } = await import("../apis/delivery.api");
  const key = status
    ? `boot:deliveries:status:${status}:${size}`
    : salesBootKeys.deliveries(size);
  return queryFetch(
    key,
    () =>
      getDeliveries({
        status: status || undefined,
        start: 0,
        size,
        hideLoader,
        silent: hideLoader,
      }),
    { force, ttlMs: BOOT_TTL_MS }
  );
}

export async function bootFetchInvoices({
  size = INVOICES_PAGE_SIZE,
  force = false,
  hideLoader = true,
  status = null,
} = {}) {
  const { getInvoices } = await import("../apis/invoice.api");
  // Status-filtered list pages use their own key so they don't collide with boot ALL.
  const key = status
    ? `boot:invoices:status:${status}:${size}`
    : salesBootKeys.invoices(size);
  return queryFetch(
    key,
    () =>
      getInvoices({
        status: status || undefined,
        start: 0,
        size,
        hideLoader,
      }),
    { force, ttlMs: BOOT_TTL_MS }
  );
}
